"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = useDocument;
const react_1 = require("react");
const native_1 = require("@react-navigation/native");
const react_redux_1 = require("react-redux");
const store_1 = require("../store");
const useDevice_1 = require("./useDevice");
const useRequest_1 = __importDefault(require("./useRequest"));
const useAnalytics_1 = __importDefault(require("./useAnalytics"));
const useConversations_1 = require("./useConversations");
const user_1 = require("../store/selectors/user");
const document_1 = require("../store/selectors/document");
const user_2 = require("../slices/user");
const document_2 = require("../slices/document");
const appState_1 = require("../slices/appState");
const rootNavigation_1 = require("../navigation/rootNavigation");
const document_3 = require("../services/document");
const application_host_1 = require("../common/application-host");
const constants_1 = require("../common/constants");
function useDocument() {
    const { navigate } = (0, native_1.useNavigation)();
    const { isMobileScreen } = (0, useDevice_1.useDevice)();
    const dispatch = (0, store_1.useAppDispatch)();
    const { request } = (0, useRequest_1.default)();
    const { trackDocumentGeneration, trackSharing } = (0, useAnalytics_1.default)();
    const { pickConversation } = (0, useConversations_1.useConversations)();
    const user = (0, react_redux_1.useSelector)(user_1.selectUser);
    const documents = (0, react_redux_1.useSelector)(document_2.selectDocumentsEntities);
    const selectedCharacter = (0, react_redux_1.useSelector)(user_1.selectCharacterProfile);
    const selectedConversation = (0, react_redux_1.useSelector)(user_1.selectConversation);
    const selectedDocumentJson = (0, react_redux_1.useSelector)(document_1.selectDocumentJson);
    const validateSourcesSize = (0, react_1.useCallback)((source) => {
        if (source.files?.some((file) => file.size / Math.pow(1024, 2) > constants_1.DOCUMENT_MAXIMUM_SIZE_MB)) {
            dispatch((0, appState_1.setAppError)(constants_1.DOCUMENT_SIZE_LIMIT_EXCCEDED));
            return false;
        }
        ;
        return true;
    }, [dispatch]);
    const generateDocument = (0, react_1.useCallback)(async (source, language, abortController) => {
        if (!validateSourcesSize(source)) {
            return false;
        }
        trackDocumentGeneration();
        dispatch((0, document_2.setDocumentUploadComplete)(false));
        dispatch((0, user_2.clearDocument)());
        request(document_3.createSummary, source, language, (streamingText) => { dispatch((0, document_2.setDocumentStreaming)(streamingText)); }, (summaryText) => { dispatch((0, document_2.setDocumentSummary)(summaryText)); }, abortController, () => dispatch((0, document_2.resetDocumentStreaming)()));
        navigate('Summary');
        return true;
    }, [validateSourcesSize, trackDocumentGeneration, dispatch, navigate, request]);
    const attachDocument = (0, react_1.useCallback)(async (source, conversationId) => {
        if (!validateSourcesSize(source)) {
            return;
        }
        ;
        return request(document_3.attachFiles, conversationId, source);
    }, [validateSourcesSize]);
    const followDocument = (0, react_1.useCallback)(async (shareId) => {
        return request(document_3.followUp, shareId).then((document) => {
            if (document) {
                if (!document.character.jsonPath) {
                    dispatch((0, document_2.addDocument)(document));
                    return document;
                }
                else {
                    return fetch(document.character.jsonPath)
                        .then((response) => response.json())
                        .then((json) => {
                        dispatch((0, document_2.setDocumentJson)({
                            ...json,
                            jsonPath: document.character.jsonPath,
                        }));
                        return document;
                    });
                }
            }
        });
    }, [dispatch, request]);
    const startDocumentConversation = (0, react_1.useCallback)(async ({ shareId, documentJson }) => {
        if (isMobileScreen && rootNavigation_1.navigationRef.current?.getCurrentRoute()?.name !== 'SummaryChat') {
            return navigate('SummaryChat', {
                ...documentJson && { conversationId: documentJson.conversationId },
                ...shareId && { shareId },
            });
        }
        else {
            dispatch((0, document_2.setIsConversationOpen)(true));
            if (documentJson) {
                return pickConversation(documentJson.character_id);
            }
            else if (shareId) {
                const isOwnDocument = (selectedDocumentJson
                    && 'sharedBy' in selectedDocumentJson
                    && selectedDocumentJson.sharedBy === user?.id);
                const followedDocument = !isOwnDocument && await request(followDocument, shareId);
                dispatch((0, user_2.setCharacter)(isOwnDocument
                    ? documents[selectedDocumentJson.characterId]?.character
                    : followedDocument && followedDocument.character));
                dispatch((0, user_2.setConversation)(isOwnDocument
                    ? documents[selectedDocumentJson.characterId]?.conversation
                    : followedDocument && followedDocument.conversation));
            }
        }
    }, [
        selectedDocumentJson,
        documents,
        user,
        isMobileScreen,
        pickConversation,
        followDocument,
        dispatch,
        request,
        navigate,
    ]);
    const showShareLink = (0, react_1.useCallback)((document) => {
        trackSharing();
        if ('shareId' in document) {
            const shareId = document.shareId;
            dispatch((0, document_2.setDocumentShareState)({
                isSharing: true,
                sharingType: document.sharedBy === user?.id ? 'owner' : 'public',
                shareUrl: `${application_host_1.DOCS_HOST}/${ /* shareId === PROJECT_2025_SHAREID ? 'project2025' :  */shareId}`,
                documentName: ('characterName' in document ? document.characterName : document.character.characterName),
            }));
        }
        else {
            dispatch((0, document_2.shareDocument)({ document }));
        }
    }, [user, trackSharing, dispatch]);
    return {
        generateDocument,
        attachDocument,
        followDocument,
        startDocumentConversation,
        showShareLink,
    };
}
;
